import { PaymentForm } from '@models/MLTC_R3/Payment.model';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Step5State {
  referenceNo: string;
  policyNo: string;
  agentDetailResponseRaw: any;
  agentInformationResponseRaw: any;
  isCompleted: boolean;
  cardHolderName: string;
  bankId: number;
  otherBank: string;
  premiumPaymentMethod: any;

  //Custom
  isPaymentInfoCompleted: boolean;
  paymentInfoData: PaymentForm | null;
  isCPFOnly: boolean;

  //Payment checking
  paymentStatus: any;

  showArcPage?: boolean;

  //promo code
  promoCode: string;
  validPromoCode: string;

  //QR LTC
  qrClientDetails: any;
  clntNum: string;
  hasShowQREligiblePopup: boolean;
  careShieldPlanName: string;
  QRTerm: string;
  QRMonthlyBenefit: string;
  resetQRData: boolean;

  //EB LTC
  EBCampaignCompanyName: string;
  qrLTCDiscountDescription: string;

  //QR+EB LTC
  ebCampaignLookupComplete: boolean;

  // QB-AA-Tracking fix: set once the initial `?cc=` campaign-code lookup (direct EBLTC
  // link, no QR) in index.tsx's checkCampaignCode() has settled - success or failure -
  // so BasicDetails.tsx can tell "still loading" apart from "resolved to no campaign"
  // before deciding whether the EB modal is about to show.
  ccCampaignLookupComplete: boolean;
}

const initialState: Step5State = {
  agentDetailResponseRaw: null,
  agentInformationResponseRaw: null,
  cardHolderName: "",
  isCompleted: false,
  bankId: 0,
  otherBank: "",
  referenceNo: "",
  policyNo: "",
  premiumPaymentMethod: null,

  //Custom
  isPaymentInfoCompleted: false,
  paymentInfoData: new PaymentForm(null),
  isCPFOnly: false,

  //Payment checking
  paymentStatus: "",

  showArcPage: false,

  //promo code
  promoCode: "",
  validPromoCode: "",

  //QR LTC
  
  qrClientDetails: null,
  clntNum: "",
  hasShowQREligiblePopup: false,
  careShieldPlanName: "",
  QRTerm: "",
  QRMonthlyBenefit: "",
  resetQRData: false,

  //EB LTC

  EBCampaignCompanyName: "",
  qrLTCDiscountDescription: "",

  //QR+EB LTC
  ebCampaignLookupComplete: false,
  ccCampaignLookupComplete: false,
};

const step5Slice = createSlice({
  name: "MLTC-R3-Step5",
  initialState,
  reducers: {
    updateReferenceNo: (state, action: PayloadAction<string>) => {
      state.referenceNo = action.payload;
    },
    updatePolicyNo: (state, action: PayloadAction<string>) => {
      state.policyNo = action.payload;
    },
    updateAgentDetailResponseRaw: (state, action: PayloadAction<any>) => {
      state.agentDetailResponseRaw = action.payload;
    },
    completeStep: (state, action: PayloadAction<boolean>) => {
      state.isCompleted = action.payload;
    },
    completePaymentInfoStep: (state, action: PayloadAction<boolean>) => {
      state.isPaymentInfoCompleted = action.payload;
    },
    updateCardHolderName: (state, action: PayloadAction<string>) => {
      state.cardHolderName = action.payload;
    },
    updateBankId: (state, action: PayloadAction<number>) => {
      state.bankId = action.payload;
    },
    updateOtherBank: (state, action: PayloadAction<string>) => {
      state.otherBank = action.payload;
    },
    updatePremiumPaymentMethod: (state, action: PayloadAction<any>) => {
      state.premiumPaymentMethod = action.payload;
    },
    updatePaymentInfoData: (state, action: PayloadAction<PaymentForm>) => {
      state.paymentInfoData = action.payload;
    },
    updateIsCPFOnly: (state, action: PayloadAction<boolean>) => {
      state.isCPFOnly = action.payload;
    },
    updatePaymentStatus: (state, action: PayloadAction<any>) => {
      state.paymentStatus = action.payload;
    },
    updateShowArcPage: (state, action: PayloadAction<boolean>) => {
      state.showArcPage = action.payload;
    },
    updateAgentInformation: (state, action: PayloadAction<any>) => {
      state.agentInformationResponseRaw = action.payload;
    },
    updatePromoCode: (state, action: PayloadAction<string>) => {
      state.promoCode = action.payload;
    },
    updateQRClntNum: (state, action: PayloadAction<string>) => {
      state.clntNum = action.payload;
    },
    setHasShownQREligiblePopup(state, action) {
      state.hasShowQREligiblePopup = action.payload;
    },
    updateCareShieldPlanName: (state, action: PayloadAction<string>) => {
      state.careShieldPlanName = action.payload;
    },
    updateQRTerm: (state, action: PayloadAction<string>) => {
      state.QRTerm = action.payload;
    },
    updateQRMonthlyBenefit: (state, action: PayloadAction<string>) => {
      state.QRMonthlyBenefit = action.payload;
    },
    updateValidPromoCodeValidity: (state, action: PayloadAction<string>) => {
      state.validPromoCode = action.payload;
    },
    updateQRClientDetails: (state, action: PayloadAction<any>) => {
      state.qrClientDetails = action.payload;
    },
    resetQRData: (state, action: PayloadAction<any>) => {
      state.resetQRData = action.payload;
    },
    updateEBCampaignCompanyName: (state, action: PayloadAction<string>) => {
      state.EBCampaignCompanyName = action.payload;
    },
    updateQRLTCDiscountDescription: (state, action: PayloadAction<string>) => {
      state.qrLTCDiscountDescription = action.payload;
    },
    setEBCampaignLookupComplete: (state, action: PayloadAction<boolean>) => {
      state.ebCampaignLookupComplete = action.payload;
    },
    setCcCampaignLookupComplete: (state, action: PayloadAction<boolean>) => {
      state.ccCampaignLookupComplete = action.payload;
    },
  },
  extraReducers: {},
});

export const {
  updateAgentDetailResponseRaw,
  updateQRClientDetails,
  completeStep,
  updateReferenceNo,
  updatePolicyNo,
  updateCardHolderName,
  updateBankId,
  updateOtherBank,
  completePaymentInfoStep,
  updatePremiumPaymentMethod,
  updatePaymentInfoData,
  updateIsCPFOnly,
  updatePaymentStatus,
  updateShowArcPage,
  updateAgentInformation,
  updatePromoCode,
  updateQRClntNum,
  setHasShownQREligiblePopup,
  updateCareShieldPlanName,
  updateQRTerm,
  resetQRData,
  updateQRMonthlyBenefit,
  updateValidPromoCodeValidity,
  updateEBCampaignCompanyName,
  updateQRLTCDiscountDescription,
  setEBCampaignLookupComplete,
  setCcCampaignLookupComplete
} = step5Slice.actions;

export default step5Slice.reducer;
